import React, { useState } from "react";
import Layout from "../layout/Layout";
import { Link, useParams } from "react-router-dom";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import { UserSelectSingle } from "../../../components/form-v2/searchable-select/UserSelect";
import { UserDataTypes } from "../../../types/user-data-types";
import { BlueButton } from "../../../components/ui/Buttons";
import {
    useDepartment,
    useDepartmentGuardians,
    useAddGuardian,
    useRemoveGuardian,
} from "../../../api/platform/useDepartments";

const DepartmentGuardians: React.FC = () => {
    const { id = "" } = useParams();

    const [user, setUser] = useState<UserDataTypes>();

    const { data: department, isLoading: loadingDepartment } = useDepartment(id);
    const { data: guardians, isLoading: loadingGuardians } = useDepartmentGuardians(id);

    const addGuardianMutation = useAddGuardian(id);
    const removeGuardianMutation = useRemoveGuardian(id);

    const addGuardian = async () => {
        if (!user?.id) return;
        await addGuardianMutation.mutateAsync(user.id);
        setUser(undefined);
    };

    const removeGuardian = async (userID: string) => {
        await removeGuardianMutation.mutateAsync(userID);
    };

    const loading = loadingDepartment || loadingGuardians;

    return (
        <Layout active={"departments"}>
            <div className="flex flex-col gap-8">
                {loading && (
                    <div className="bg-white shadow-md p-4 w-1/2 flex flex-col gap-8 items-center">
                        <LoadingSpinner />
                    </div>
                )}
                {!loading && (
                    <div className="flex flex-row gap-8">
                        <div className="bg-white shadow-md p-4 flex flex-col gap-8 flex-1">
                            <p className="text-md text-left pb-2 font-semibold">
                                Manage Guardians ({department?.name})
                            </p>
                            <div className="grid grid-cols-[30%_50%_20%]">
                                <div>Name</div>
                                <div>Email</div>
                                <div>Action</div>
                            </div>
                            <hr />
                            {guardians?.map((g) => (
                                <div key={g.id} className="grid grid-cols-[30%_50%_20%]">
                                    <div className="overflow-hidden overflow-ellipsis pr-4">
                                        <Link to={`/management/users/edit/${g.id}`}>{g.name}</Link>
                                    </div>
                                    <div className="overflow-hidden overflow-ellipsis pr-4">
                                        <Link to={`/management/users/edit/${g.id}`}>{g.email}</Link>
                                    </div>
                                    <div>
                                        <BlueButton
                                            text="Remove"
                                            onClick={() => removeGuardian(g.id)}
                                            disabled={removeGuardianMutation.isLoading}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="bg-white shadow-md p-4 flex flex-col gap-8 w-72">
                            <p className="text-md text-left pb-2 font-semibold">Add Guardian</p>
                            <UserSelectSingle
                                clientID={department?.clientID ?? ""}
                                accessLevel="department"
                                excludeIDs={guardians?.map((g) => g.id)}
                                user={user}
                                onChange={setUser}
                            />
                            <BlueButton
                                text="Add Guardian"
                                disabled={!user || addGuardianMutation.isLoading}
                                onClick={addGuardian}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

DepartmentGuardians.displayName = "DepartmentGuardians";

export default DepartmentGuardians;
